import { getTHERMSImageThumbnailUrl } from "../../../Files/utils/get-therms-image-thumbnail-url.js";
import { AppShellViewModel } from "../../viewmodels/AppShellViewModel.js";
import { SidePanelViewModel } from "../../viewmodels/SidePanelViewModel.js";
import { NotificationsViewModel } from "../../../Notifications/viewmodels/NotificationsViewModel.js";
import { useNewSystemInfoCount } from "../../../SystemInfo/shared/hooks/useNewSystemInfoCount.js";
import { MailSidebarScreen } from "../../../Mail/screens/MailSidebarScreen/index.js";
import { NotificationsSidebarScreen } from "../../../Notifications/screens/NotificationsSidebarScreen/index.js";
import { UserSidebarScreen } from "../../../Users/screens/UserSidePanelScreen.js";
import { OrganizationSidebarScreen } from "../../../Organizations/screens/OrganizationSidebarScreen.js";
import { SystemInfoSidebarScreen } from "../../../SystemInfo/shared/screens/SystemInfoSidebarScreen/index.js";
import { useMemo } from "react";
import { Avatar, Badge, Button, ButtonGroup, Icon, SlidePanel, useAtalayaContext, useIsBreakpointActive } from "@therms/atalaya";
import { IoClose } from "react-icons/io5";
import { IoMdMail } from "react-icons/io";
import { jsx, jsxs } from "react/jsx-runtime";
import { useSingleton } from "mvc-kit/react";
import { MdCampaign, MdDarkMode, MdLightMode, MdLocalPolice, MdNotifications, MdPushPin } from "react-icons/md";
// src/modules/AppWrapper2/components/SidePanel/SidePanel.tsx
var contentComponents = {
	mail: /* @__PURE__ */ jsx(MailSidebarScreen, {}),
	notifications: /* @__PURE__ */ jsx(NotificationsSidebarScreen, {}),
	organization: /* @__PURE__ */ jsx(OrganizationSidebarScreen, {}),
	systemInfo: /* @__PURE__ */ jsx(SystemInfoSidebarScreen, {}),
	user: /* @__PURE__ */ jsx(UserSidebarScreen, {})
};
function SidePanel() {
	const shellVM = useSingleton(AppShellViewModel);
	const panelVM = useSingleton(SidePanelViewModel);
	const notificationsVM = useSingleton(NotificationsViewModel);
	const isWideScreen = useIsBreakpointActive("md");
	const { isDarkMode, toggleDarkMode } = useAtalayaContext();
	const newSystemInfoCount = useNewSystemInfoCount();
	const user = shellVM.user;
	const hasUnreadMail = panelVM.hasUnreadMail;
	const unreadNotifications = notificationsVM.unreadCount;
	const SidePanelContent = useMemo(() => /* @__PURE__ */ jsx("div", {
		className: "relative flex-1 overflow-y-auto overscroll-contain",
		children: !!panelVM.state.content && contentComponents[panelVM.state.content]
	}), [panelVM.state.content]);
	if (isWideScreen && panelVM.state.isOpen && panelVM.state.isPinned) return /* @__PURE__ */ jsx("div", {
		className: "z-fixed bg-surface-subtle flex h-full w-[450px] shrink-0 flex-col",
		children: SidePanelContent
	});
	return /* @__PURE__ */ jsx(SlidePanel, {
		active: !!panelVM.state.isOpen,
		onClose: panelVM.close,
		showCloseButton: false,
		width: !isWideScreen ? "100vw" : void 0,
		children: /* @__PURE__ */ jsxs("div", {
			className: "bg-surface-subtle flex h-full flex-col shadow-xl",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "pt-base px-base gap-x-base inline-flex w-full justify-between",
				children: [/* @__PURE__ */ jsx("div", {
					className: "bg-surface rounded-sm",
					children: /* @__PURE__ */ jsxs(ButtonGroup, {
						subtle: true,
						children: [
							/* @__PURE__ */ jsx(Button, {
								disabled: panelVM.state.content === "user",
								onClick: () => panelVM.setContent("user"),
								children: /* @__PURE__ */ jsx(Avatar, {
									url: getTHERMSImageThumbnailUrl(user?.avatarUrl),
									statusColor: user?.status?.color,
									circular: true,
									size: "xs"
								})
							}),
							/* @__PURE__ */ jsx(Button, {
								disabled: panelVM.state.content === "organization",
								onClick: () => panelVM.setContent("organization"),
								children: /* @__PURE__ */ jsx(Icon, { Icon: MdLocalPolice })
							}),
							/* @__PURE__ */ jsx(Button, {
								disabled: panelVM.state.content === "notifications",
								onClick: () => panelVM.setContent("notifications"),
								children: /* @__PURE__ */ jsxs("div", {
									className: "relative",
									children: [/* @__PURE__ */ jsx(Icon, { Icon: MdNotifications }), unreadNotifications > 0 && /* @__PURE__ */ jsx("div", {
										className: "absolute -top-2 -right-0.5 text-right",
										children: /* @__PURE__ */ jsx(Badge, {
											size: "sm",
											subtle: false,
											max: 99,
											value: unreadNotifications,
											variant: "critical"
										})
									})]
								})
							}),
							/* @__PURE__ */ jsx(Button, {
								disabled: panelVM.state.content === "mail",
								onClick: () => panelVM.setContent("mail"),
								children: /* @__PURE__ */ jsxs("div", {
									className: "relative",
									children: [/* @__PURE__ */ jsx(Icon, { Icon: IoMdMail }), hasUnreadMail && /* @__PURE__ */ jsx("div", { className: `absolute top-0 right-0 rounded-full p-1 ${panelVM.state.content === "mail" ? "bg-info-dark" : "bg-info"}` })]
								})
							}),
							/* @__PURE__ */ jsx(Button, {
								disabled: panelVM.state.content === "systemInfo",
								onClick: () => panelVM.setContent("systemInfo"),
								children: /* @__PURE__ */ jsxs("div", {
									className: "relative",
									children: [/* @__PURE__ */ jsx(Icon, { Icon: MdCampaign }), !!newSystemInfoCount && /* @__PURE__ */ jsx("div", { className: `absolute top-0 right-0 rounded-full p-1 ${panelVM.state.content === "systemInfo" ? "bg-main-dark" : "bg-main"}` })]
								})
							})
						]
					})
				}), /* @__PURE__ */ jsxs(ButtonGroup, {
					subtle: true,
					children: [
						/* @__PURE__ */ jsx(Button, {
							onClick: toggleDarkMode,
							children: /* @__PURE__ */ jsx(Icon, { Icon: isDarkMode ? MdDarkMode : MdLightMode })
						}),
						isWideScreen && /* @__PURE__ */ jsx(Button, {
							onClick: panelVM.pin,
							children: /* @__PURE__ */ jsx(Icon, { Icon: MdPushPin })
						}),
						/* @__PURE__ */ jsx(Button, {
							onClick: panelVM.close,
							children: /* @__PURE__ */ jsx(Icon, { Icon: IoClose })
						})
					]
				})]
			}), SidePanelContent]
		})
	});
}
export { SidePanel };

//# sourceMappingURL=SidePanel.js.map